import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Button,
  Skeleton,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";
import { Fence, ChevronRight, Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SKELETON_ROWS = [0, 1, 2, 3, 4];
const MAX_SHOWN = 5;

const GeofencePresencePanel = memo(({ geofences = [], loading = false }) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleViewAll = useCallback(() => navigate("/geofence"), [navigate]);
  const handleRowClick = useCallback(
    (id) => navigate(`/geofence/${id}/view`, { state: { from: "/" } }),
    [navigate]
  );

  const top = [...geofences]
    .sort((a, b) => (b.presence_count ?? 0) - (a.presence_count ?? 0))
    .slice(0, MAX_SHOWN);

  const totalInside = geofences.reduce(
    (sum, g) => sum + (g.presence_count ?? 0),
    0
  );

  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
              : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)"
          }`,
          position: "relative",
          overflow: "hidden",
          width: "100%",
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: theme.palette.primary.main,
            opacity: 0.8,
          }}
        />

        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          mt={1}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <Fence />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle1" component="h2" fontWeight={600}>
                  {t("geofences.name", { defaultValue: "Geofences" })}
                </Typography>
                {totalInside > 0 && (
                  <Chip
                    label={`${totalInside} inside`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: theme.palette.success.main,
                      color: "#fff",
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t("geofences.top_by_presence", {
                  defaultValue: "Top geofences by device presence",
                })}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={handleViewAll}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              borderColor: alpha(theme.palette.divider, 0.3),
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            {t("common.view_all", { defaultValue: "View all" })}
          </Button>
        </Box>

        <Divider sx={{ mb: 1, opacity: 0.5 }} />

        {loading ? (
          <List disablePadding>
            {SKELETON_ROWS.map((i) => (
              <ListItem key={i} sx={{ px: 0, py: 1 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Box sx={{ ml: "auto" }}>
                  <Skeleton variant="rounded" width={48} height={22} />
                </Box>
              </ListItem>
            ))}
          </List>
        ) : (
          <List disablePadding>
            {top.map((geofence, idx) => {
              const count = geofence.presence_count ?? 0;
              const isActive =
                geofence.active === 1 || geofence.active === true;
              return (
                <Box key={geofence.id}>
                  <ListItem
                    disablePadding
                    sx={{
                      py: 1,
                      px: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "background 0.15s",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.05
                        ),
                      },
                    }}
                    onClick={() => handleRowClick(geofence.id)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        mr: 1.5,
                        backgroundColor: alpha(
                          theme.palette.text.secondary,
                          0.08
                        ),
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                      >
                        {idx + 1}
                      </Typography>
                    </Box>
                    <ListItemText
                      primary={geofence.name}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                        noWrap: true,
                      }}
                      secondary={
                        !isActive
                          ? t("common.inactive", { defaultValue: "Inactive" })
                          : null
                      }
                      secondaryTypographyProps={{
                        variant: "caption",
                        color: "text.disabled",
                      }}
                    />
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ flexShrink: 0, ml: 1 }}
                    >
                      <Chip
                        label={
                          count === 1 ? `${count} device` : `${count} devices`
                        }
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor:
                            count > 0
                              ? alpha(theme.palette.success.main, 0.12)
                              : alpha(theme.palette.text.secondary, 0.08),
                          color:
                            count > 0
                              ? theme.palette.success.main
                              : theme.palette.text.secondary,
                        }}
                      />
                      <ChevronRight
                        sx={{
                          fontSize: 16,
                          color: theme.palette.text.disabled,
                        }}
                      />
                    </Box>
                  </ListItem>
                  {idx < top.length - 1 && (
                    <Divider sx={{ opacity: 0.3, mx: 1 }} />
                  )}
                </Box>
              );
            })}
          </List>
        )}
      </Paper>
    </Fade>
  );
});

GeofencePresencePanel.displayName = "GeofencePresencePanel";

export default GeofencePresencePanel;
